// ═══════════════════════════════════════════════════════════════
// CATÁLOGO DE TIPOS DE CASO — State Machine declarativa + Multi-SLA
// ═══════════════════════════════════════════════════════════════
//
// Paralelo a `tiposTarea.js` pero adaptado a Casos:
//   - Multi-SLA por tipo (no un único slaDefault como en tareas)
//   - State Machine declarativa por tipo (cada tipo define sus
//     estados disponibles + transiciones permitidas)
//   - Sin overrides per-empresa (los SLAs vienen del tipoCaso
//     directamente; si una empresa necesita su propio SLA, se
//     crea un tipoCaso específico)
//
// Colección: tiposCaso (top-level)
//   { codigo, nombre, descripcion, area, moduloOrigen,
//     estadosDisponibles, estadoInicial, transiciones,
//     slaDefaults, prioridadDefault, asignacionDefault,
//     camposObligatorios, autoResponse, plantillaInicial,
//     activo, orden }
//
//   estadosDisponibles  — array de estados que este tipo soporta
//   estadoInicial       — estado al crear caso de este tipo
//   transiciones        — map { estadoActual: [estadosPermitidos] }
//   slaDefaults         — array [{ tipo, minutos, ventanaAlertaMin }]
//   asignacionDefault   — { rol?, userId? } regla inicial
//   autoResponse        — { activo, plantillaId } comentario público
//                          automático al crear
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, query, where,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'

// Estados base soportados por el motor (cada tipoCaso elige subset)
export const CASO_ESTADOS = {
  NUEVO:              'nuevo',
  ABIERTO:            'abierto',
  EN_INVESTIGACION:   'en-investigacion',
  EN_ESPERA_EXTERNA:  'en-espera-externa',
  ESCALADO:           'escalado',
  EN_REVISION:        'en-revision',
  RESUELTO:           'resuelto',
  CERRADO:            'cerrado',
  CERRADO_SIN_ACCION: 'cerrado-sin-accion',
  REABIERTO:          'reabierto',
}

// Tipos de SLA soportados
export const CASO_SLA_TIPOS = {
  PRIMERA_RESPUESTA:  'primera-respuesta',
  RESOLUCION:         'resolucion',
  RESPUESTA_EXTERNA:  'respuesta-externa',
  CUSTOM:             'custom',
}

// ──────── ABM del catálogo ────────

/** Listar todos los tipos de caso (opcionalmente solo activos). */
export async function listarTiposCaso({ soloActivos = true } = {}) {
  const snap = await getDocs(collection(db, COLLECTIONS.TIPOS_CASO))
  let list = snap.docs.map(docConId)
  if (soloActivos) list = list.filter(t => t.activo !== false)
  list.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return list
}

/** Obtener un tipo de caso por ID. */
export async function getTipoCaso(tipoCasoId) {
  const snap = await getDoc(doc(db, COLLECTIONS.TIPOS_CASO, tipoCasoId))
  return snap.exists() ? docConId(snap) : null
}

/** Buscar tipo de caso por código (ej: 'reclamo-ml'). */
export async function getTipoCasoPorCodigo(codigo) {
  const q = query(collection(db, COLLECTIONS.TIPOS_CASO), where('codigo', '==', codigo))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return docConId(d)
}

/** Crear o actualizar un tipo de caso. */
export async function upsertTipoCaso(tipoCasoId, datos) {
  // Validar transiciones bien-formadas
  const transiciones = datos.transiciones || {}
  for (const [from, tos] of Object.entries(transiciones)) {
    if (!Array.isArray(tos)) {
      throw new Error(`[tiposCaso] transiciones["${from}"] debe ser un array de estados destino`)
    }
  }

  const data = {
    codigo:             datos.codigo || null,
    nombre:             datos.nombre || 'Tipo de caso',
    descripcion:        datos.descripcion || '',
    area:               datos.area || null,
    moduloOrigen:       datos.moduloOrigen || null,
    estadosDisponibles: Array.isArray(datos.estadosDisponibles) ? datos.estadosDisponibles
                          : Object.values(CASO_ESTADOS),
    estadoInicial:      datos.estadoInicial || CASO_ESTADOS.ABIERTO,
    transiciones:       transiciones,
    slaDefaults:        Array.isArray(datos.slaDefaults) ? datos.slaDefaults : [],
    prioridadDefault:   datos.prioridadDefault || 'normal',
    severidadDefault:   datos.severidadDefault || 's3',
    asignacionDefault:  datos.asignacionDefault || null,
    camposObligatorios: Array.isArray(datos.camposObligatorios) ? datos.camposObligatorios : [],
    autoResponse:       datos.autoResponse || null,
    plantillaInicial:   datos.plantillaInicial || null,
    requiereAprobacion: !!datos.requiereAprobacion,
    activo:             datos.activo !== false,
    orden:              Number(datos.orden) || 100,
  }

  if (!tipoCasoId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COLLECTIONS.TIPOS_CASO), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COLLECTIONS.TIPOS_CASO, tipoCasoId), payload)
  return { id: tipoCasoId, ...payload }
}

/** Desactivar (soft delete) un tipo de caso. */
export async function desactivarTipoCaso(tipoCasoId) {
  await updateDoc(doc(db, COLLECTIONS.TIPOS_CASO, tipoCasoId), forUpdate({ activo: false }))
}

// ──────── State Machine ────────

/**
 * Obtiene la lista de estados destino permitidos desde un estado actual,
 * según las transiciones declaradas en el tipoCaso.
 *
 * @param {Object} tipoCaso       — doc completo de tipoCaso
 * @param {string} estadoActual
 * @returns {string[]} estados a los que se puede transicionar
 */
export function getTransicionesPermitidas(tipoCaso, estadoActual) {
  if (!tipoCaso?.transiciones) return []
  return tipoCaso.transiciones[estadoActual] || []
}

/**
 * Valida si una transición de estado es legal según el tipoCaso.
 *
 * @param {Object} tipoCaso       — doc completo de tipoCaso
 * @param {string} estadoActual
 * @param {string} estadoNuevo
 * @returns {boolean}
 */
export function validarTransicion(tipoCaso, estadoActual, estadoNuevo) {
  if (estadoActual === estadoNuevo) return true  // no-op permitido
  const permitidos = getTransicionesPermitidas(tipoCaso, estadoActual)
  return permitidos.includes(estadoNuevo)
}

// ──────── Resolución de SLAs (multi-track) ────────

/**
 * Devuelve los SLAs default declarados para este tipoCaso.
 * Cada SLA es { tipo, minutos, ventanaAlertaMin }.
 *
 * Nota: a diferencia de tiposTarea (un único slaDefault + overrides
 * per-empresa), los tiposCaso declaran su propio array de SLAs y NO
 * hay overrides per-empresa. Si una empresa necesita SLAs distintos,
 * se crea un tipoCaso específico.
 *
 * @param {Object} tipoCaso
 * @returns {Array<{tipo, minutos, ventanaAlertaMin}>}
 */
export function resolverSLAsCaso(tipoCaso) {
  if (!tipoCaso?.slaDefaults) return []
  return tipoCaso.slaDefaults.map(s => ({
    tipo: s.tipo,
    minutos: Number(s.minutos),
    ventanaAlertaMin: Number(s.ventanaAlertaMin) || 15,
  }))
}

// ──────── Exponer en window (admin debug) ────────

if (typeof window !== 'undefined') {
  window.__tiposCaso = {
    listarTiposCaso,
    getTipoCaso,
    getTipoCasoPorCodigo,
    upsertTipoCaso,
    desactivarTipoCaso,
    getTransicionesPermitidas,
    validarTransicion,
    resolverSLAsCaso,
    CASO_ESTADOS,
    CASO_SLA_TIPOS,
  }
}
